/**
 * BCP Page Context Hook
 * Centralizes URL parsing, location state, and edit mode determination
 */

import { useLocation, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import type { BCPPageContext } from '../types/navigation';
import type { BCPType } from '../types/bcp';
import { navigationService } from '../utils/navigation';

export const useBCPPageContext = (): BCPPageContext => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // URL Parameters
  const bcpId = searchParams.get('bcpId');
  const tenantId = searchParams.get('tenantId');
  
  // Location State
  const state = navigationService.extractLocationState<{
    uploadedFiles: any[];
    apiResponse?: any;
    bcpType?: BCPType;
    companyInfo?: {
      companyName: string;
      location: string;
      employeeCount?: string;
      businessDescription: string;
      criticalBusiness: string;
    };
    restorePolicyData?: Record<string, string>;
  }>(location, {
    uploadedFiles: [],
    apiResponse: undefined,
    bcpType: 'simple'
  });
  
  // Computed values with proper memoization
  const context = useMemo((): BCPPageContext => {
    const isEditMode = Boolean(bcpId);
    const hasUploadedFiles = state.uploadedFiles && state.uploadedFiles.length > 0;

    return {
      // URL Parameters
      bcpId,
      tenantId,

      // Navigation State
      uploadedFiles: state.uploadedFiles || [],
      apiResponse: state.apiResponse,
      bcpType: state.bcpType || 'simple',
      companyInfo: state.companyInfo,
      restorePolicyData: state.restorePolicyData,

      // Computed State
      isEditMode,
      hasUploadedFiles,
    };
  }, [bcpId, tenantId, state.uploadedFiles, state.apiResponse, state.bcpType, state.companyInfo, state.restorePolicyData]);
  
  return context;
};